import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, Inbox, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtNotifTime, fmtDateTime } from "@/lib/format";
import { Loading, ErrorView } from "@/components/StateViews";
import { NotifIcon } from "@/components/NotificationBell";
import PageSizeSelector from "@/components/PageSizeSelector";

const TABS = [{ k: "all", label: "All" }, { k: "unread", label: "Unread" }];

function groupByDay(items) {
  const groups = {};
  const today = new Date().toDateString();
  const yest = new Date(Date.now() - 86400000).toDateString();
  items.forEach((n) => {
    const d = n.created_at ? new Date(n.created_at).toDateString() : "—";
    const label = d === today ? "Today" : d === yest ? "Yesterday" : (n.created_at ? new Date(n.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "Earlier");
    (groups[label] = groups[label] || []).push(n);
  });
  return groups;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/notifications", { params: { filter: tab, page, page_size: pageSize } }); setData(data.data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, page, pageSize]);
  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const goTab = (k) => { setTab(k); setPage(1); };

  const open = async (n) => {
    if (!n.read) { api.post(`/notifications/${n.id}/read`).catch(() => {}); }
    if (n.route) navigate(n.route); else load();
  };
  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); toast.success("All marked as read"); load(); };
  const dismiss = async (e, id) => { e.stopPropagation(); await api.delete(`/notifications/${id}`).catch(() => {}); load(); };
  const clearRead = async () => { const { data } = await api.delete("/notifications"); toast.success(`Cleared ${data.data.deleted} read`); load(); };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const groups = data ? groupByDay(data.items) : {};

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="notifications-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Bell className="h-7 w-7 text-loop" />Notifications</h1>
          <p className="text-sm text-ink/60">{data ? `${data.unread} unread of ${data.total}` : "Loading…"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={markAll} disabled={!data?.unread} data-testid="notif-page-mark-all" className="flex items-center gap-1.5 rounded-xl border border-sky bg-white px-3 py-2 text-sm font-medium text-navy hover:bg-mist disabled:opacity-40"><Check className="h-4 w-4" />Mark all read</button>
          <button onClick={clearRead} data-testid="notif-page-clear-read" className="flex items-center gap-1.5 rounded-xl border border-sky bg-white px-3 py-2 text-sm font-medium text-ink/60 hover:bg-mist"><Trash2 className="h-4 w-4" />Clear read</button>
        </div>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => goTab(t.k)} data-testid={`notif-tab-${t.k}`} className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === t.k ? "bg-loop text-white" : "border border-sky bg-white text-ink/70 hover:bg-mist"}`}>
            {t.label}{t.k === "unread" && data?.unread ? <span className={`ml-1.5 rounded-full px-1.5 text-[11px] font-bold ${tab === t.k ? "bg-white text-loop" : "bg-red-500 text-white"}`}>{data.unread}</span> : null}
          </button>
        ))}
      </div>

      {loading && !data ? <Loading label="Loading notifications…" /> : error ? <ErrorView message={error} onRetry={load} /> :
        !data?.items.length ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-sky bg-white py-20 text-center" data-testid="notif-empty">
            <Inbox className="mb-3 h-10 w-10 text-ink/25" />
            <p className="font-sora font-semibold text-navy">{tab === "unread" ? "No unread notifications" : "Nothing here yet"}</p>
            <p className="text-sm text-ink/45">Assignments, SLA alerts, follow-ups and reviews will appear here.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groups).map(([label, list]) => (
              <div key={label}>
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-ink/40">{label}</p>
                <div className="overflow-hidden rounded-2xl border border-sky/70 bg-white">
                  {list.map((n) => (
                    <button key={n.id} onClick={() => open(n)} data-testid={`notif-row-${n.id}`} className={`group flex w-full items-start gap-3 border-b border-sky/40 px-4 py-3.5 text-left transition-colors last:border-0 hover:bg-mist ${n.read ? "" : "bg-loop/[0.04]"}`}>
                      <NotifIcon type={n.type} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${n.read ? "font-medium text-ink/80" : "font-semibold text-navy"}`}>{n.title}</p>
                          {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-loop" />}
                        </div>
                        {n.body && <p className="mt-0.5 text-xs text-ink/55">{n.body}</p>}
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-ink/40">
                          <span title={fmtDateTime(n.created_at)}>{fmtNotifTime(n.created_at)}</span>
                          {n.route && <span className="flex items-center gap-0.5 text-loop opacity-0 transition-opacity group-hover:opacity-100">Open<ArrowRight className="h-3 w-3" /></span>}
                        </div>
                      </div>
                      <span onClick={(e) => dismiss(e, n.id)} data-testid={`notif-dismiss-${n.id}`} className="rounded-lg p-1.5 text-ink/30 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {data?.items?.length > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-ink/50">Page {data.page} of {totalPages}</p>
                <div className="flex items-center gap-3">
                  <PageSizeSelector value={pageSize} onChange={(n) => { setPageSize(n); setPage(1); }} testid="notifications-page-size" />
                  <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40">Prev</button>
                    <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40">Next</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
}
